import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { useDispatch, useSelector } from 'react-redux';

import Map3D from '../../models/Map3D';
import Constants from '../../utils/constants';
import { setViewMode } from '../../reducers/mapReducer';
import { closeTool } from '../../reducers/sidebarReducer';
import './Toggle3DControl.scss';

/**
 * Botão de alternância entre a vista 2D (OpenLayers) e a vista 3D (Cesium).
 *
 * Componente React posicionado dentro da div #map mas fora do viewport OL —
 * garante que fica clicável por cima do canvas Cesium, ao contrário de um
 * ol/control/Control, que o container do OLCesium pode tapar.
 */
function Toggle3DControl({ map }) {
  const dispatch = useDispatch();
  const { viewMode } = useSelector((state) => state.map);
  const selectedTool = useSelector((state) => state.sidebar.selectedTool);
  const map3dRef = useRef(null);

  // O Map3D pertence a uma instância de Map: quando o mapa é reconstruído
  // (refresh do ResetControl) ou o componente desmonta, tem de ser libertado.
  useEffect(
    () => () => {
      if (map3dRef.current) {
        map3dRef.current.destroy();
        map3dRef.current = null;
      }
    },
    [map],
  );

  if (!window.Cesium) {
    return null;
  }

  const is3D = viewMode === '3D';

  const handleToggle = () => {
    const entering3D = !is3D;

    if (entering3D && !map3dRef.current) {
      map3dRef.current = new Map3D(map, {
        terrainUrl: Constants.TERRAIN_URL,
        tilesetUrl: Constants.TILES3D_URL,
      });
    }

    map3dRef.current.setEnabled(entering3D);
    dispatch(setViewMode(entering3D ? '3D' : '2D'));

    if (entering3D && Constants.TOOLS_UNAVAILABLE_IN_3D.includes(selectedTool)) {
      dispatch(closeTool());
    }
  };

  return (
    <div className="toggle-3d">
      <button
        type="button"
        className={is3D ? 'active' : ''}
        title={is3D ? 'Vista 2D' : 'Vista 3D'}
        onClick={handleToggle}
      >
        <i className={is3D ? 'fa-solid fa-map' : 'fa-solid fa-cube'} />
      </button>
      {is3D && Constants.TILES3D_URL && (
        <button
          type="button"
          title="Ir para o modelo 3D"
          onClick={() => map3dRef.current && map3dRef.current.flyToTileset()}
        >
          <i className="fa-solid fa-location-arrow" />
        </button>
      )}
    </div>
  );
}

Toggle3DControl.propTypes = {
  map: PropTypes.shape({
    olMap: PropTypes.object.isRequired,
    layers: PropTypes.array.isRequired,
  }).isRequired,
};

export default Toggle3DControl;
